/**
 * Ієрархія ключів — [ADR-0016](../../../../docs/adr/0016-key-hierarchy.md).
 *
 * ```
 * парольна фраза ──Argon2id──► майстер-ключ ─┐
 * 24 слова ────────HKDF───────► ключ відновлення ─┤ обгортають
 * ключ пристрою (алг. 3, невитягуваний) ──────────┤
 * ключ-ескроу (у нас на сервері) ─────────────────┘ ← лише за згодою
 *                                                 ▼
 *                                       ключ даних (DEK, 32 Б)
 *                                                 │ HKDF
 *                                                 └─► ключ запису
 * ```
 *
 * ⚠️ Четвертий рядок відрізняється від трьох перших **тим, хто тримає
 * ключ**: три перші тримає людина, ескроу — наш сервер
 * ([ADR-0036](../../../../docs/adr/0036-google-sign-in.md), варіант Б).
 * Тобто для акаунта, який увімкнув вхід через Google, обіцянка з
 * [ADR-0002] «сервер не має ключа й не може мати» перестає бути правдою.
 * Ховати це в коментарі до типу не можна, і воно не сховане: обгортка
 * зʼявляється лише там, де її попросили, а прибрати її — це прибрати
 * один елемент масиву.
 *
 * Головна властивість: **ключ даних один на акаунт і не змінюється**.
 * Обгорток у нього кілька — по одній на кожен спосіб доступу. Тому
 * додати пристрій означає додати обгортку, а не перешифрувати архів;
 * втратити пристрій означає втратити одну обгортку з кількох.
 *
 * Типи ключів навмисно різні (`MasterKey`, `DataKey`, …), хоча всі вони —
 * 32 байти. Помилка в шифруванні не падає з винятком: підставити не той
 * 32-байтний масив можна мовчки й помітити через роки. Хай компілятор
 * ловить те, чого не зловлять тести.
 */
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { randomBytes } from "@noble/hashes/utils.js";
import { openAead, purpose, sealAead } from "./aead.js";
/** Усі симетричні ключі схеми — 256 біт. */
export const KEY_BYTES = 32;
/**
 * Позначити байти ключем. Єдине місце в коді, де позначка зʼявляється, —
 * і тому єдине місце, яке варто читати уважно.
 */
export function asKey(bytes) {
    if (bytes.length !== KEY_BYTES) {
        throw new Error(`ключ має бути ${KEY_BYTES} байтів, отримано ${bytes.length}`);
    }
    return bytes;
}
/** Новий ключ даних. Джерело випадковості — CSPRNG платформи. */
export function newDataKey() {
    return asKey(randomBytes(KEY_BYTES));
}
/**
 * Новий ключ-ескроу. Ті самі 32 байти з CSPRNG, що й ключ даних, — і
 * навмисно без жодного звʼязку з акаунтом Google.
 *
 * Спокуса вивести його з `sub` або пошти виглядає ощадливою (нічого не
 * треба зберігати) і є найгіршим із можливих варіантів: `sub` не таємниця,
 * і тоді архів відкривав би кожен, хто цей ідентифікатор знає. Ескроу
 * мусить бути справжньою випадковістю, яку хтось **зберігає**.
 */
export function newEscrowKey() {
    return asKey(randomBytes(KEY_BYTES));
}
/**
 * Ключ конкретного запису: HKDF-SHA256 від ключа даних із його
 * ідентифікатором у полі `info`.
 *
 * Навіщо окремий ключ на запис, коли можна шифрувати все ключем даних:
 * ключ запису можна віддати комусь разом з одним записом (спільний доступ,
 * експорт одного дня), не віддаючи всього архіву. Це коштує один HKDF на
 * відкриття запису — мікросекунди.
 *
 * Солі немає навмисно: HKDF без солі коректний, коли вхідний матеріал уже
 * рівномірно випадковий, а ключ даних саме такий.
 */
export function deriveEntryKey(dek, entryId) {
    return asKey(hkdf(sha256, dek, undefined, purpose("entry", "v1", entryId), KEY_BYTES));
}
/**
 * Ключ, яким обгортається закритий ключ захоплення ([ADR-0011]).
 *
 * Виводиться з ключа даних, а не з майстер-ключа, і причина в ротації:
 * ключ даних переживає зміну пароля, а майстер — ні. Прив'язавши обгортку
 * до майстра, ми зробили б так, що зміна пароля робить нечитабельною чергу,
 * записану до неї, — тобто губила б думки, які людина щойно зберегла.
 *
 * Солі немає з тієї самої причини, що й у ключа запису: вхідний матеріал
 * уже рівномірно випадковий.
 */
export function deriveCaptureWrapKey(dek) {
    return hkdf(sha256, dek, undefined, purpose("capture-wrap", "v1"), KEY_BYTES);
}
/**
 * Прив'язка обгортки до акаунта й способу доступу.
 *
 * Іде в `aad`, тобто входить у мітку автентичності й не шифрується.
 * Сенс: обгортку, зроблену ключем відновлення, не можна підсунути на місце
 * парольної — вона не розгорнеться, замість того щоб мовчки розгорнутися
 * не тим шляхом. Так само між акаунтами.
 */
function binding(accountId, holder) {
    return purpose("wrap", "v1", accountId, holder);
}
export function wrapDataKey(key, dek, accountId, holder) {
    return { holder, sealed: sealAead(key, dek, binding(accountId, holder)) };
}
/** Кидає `DecryptionFailedError`, якщо ключ, акаунт або призначення не ті. */
export function unwrapDataKey(key, wrap, accountId) {
    return asKey(openAead(key, wrap.sealed, binding(accountId, wrap.holder)));
}
/**
 * Затерти ключ у памʼяті.
 *
 * Чесно про межі: у JavaScript це прибирає байти лише з цього масиву.
 * Збирач сміття міг скопіювати їх куди завгодно, і дістати ті копії
 * ми не можемо. Тому це зменшення вікна, а не гарантія — на відміну
 * від `sodium_memzero`, на який посилається docs/05.
 */
export function wipe(key) {
    key.fill(0);
}
//# sourceMappingURL=keys.js.map